import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { AccountPageHeader } from '../../account/components/AccountPageHeader';
import { ShippingAddressForm } from '../components/ShippingAddressForm';
import { useShippingAddress, useUpdateShippingAddress } from '../shipping.services';
import { Skeleton, Button } from '@shopmuchmore/design-system';
import type { UpdateShippingAddressInput } from '../shipping.types';

export function ShippingAddressEditPage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const { data: address, isLoading: isFetching } = useShippingAddress(slug || '');
  const updateMutation = useUpdateShippingAddress();

  const handleSubmit = async (data: any) => {
    if (!slug) return;
    try {
      const payload: UpdateShippingAddressInput = {
        ...data,
        shipping_slug: slug,
      };
      await updateMutation.mutateAsync(payload);
      toast.success('Shipping address updated successfully');
      navigate('/account/addresses');
    } catch (error) {
      toast.error('Failed to update shipping address');
      console.error('Update address error:', error);
    }
  };

  const handleCancel = () => {
    navigate('/account/addresses');
  };

  if (isFetching) {
    return (
      <div className="w-full space-y-8">
        <AccountPageHeader
          title="Edit Address"
          description="Update your delivery location details."
        />
        <div className="bg-white p-8 rounded-[2.5rem] border-[1.5px] border-gray-100 max-w-3xl">
          <div className="space-y-6">
            <div className="grid grid-cols-2 gap-6">
              <Skeleton className="h-12 rounded-xl" />
              <Skeleton className="h-12 rounded-xl" />
            </div>
            <Skeleton className="h-12 rounded-xl" />
            <div className="grid grid-cols-2 gap-6">
              <Skeleton className="h-12 rounded-xl" />
              <Skeleton className="h-12 rounded-xl" />
            </div>
            <Skeleton className="h-12 rounded-xl w-32 mt-4" />
          </div>
        </div>
      </div>
    );
  }

  if (!address) {
    return (
      <div className="w-full space-y-8 text-center py-20">
        <h2 className="text-2xl font-bold">Address not found</h2>
        <Button onClick={() => navigate('/account/addresses')}>Back to Addresses</Button>
      </div>
    );
  }

  return (
    <div className="w-full space-y-8">
      <AccountPageHeader
        title="Edit Address"
        description="Update your delivery location details."
      />

      <div className="bg-white p-8 rounded-[2.5rem] border-[1.5px] border-gray-100">
        <ShippingAddressForm
          initialData={address}
          onSubmit={handleSubmit}
          onCancel={handleCancel}
          isLoading={updateMutation.isPending}
        />
      </div>
    </div>
  );
}
